import type { GameEvent, World } from '../sim/types';
import { COMMUNITIES, SPECIES, displayName, membersOf, sentenceName, speciesOf, type CommunityId, type SpeciesRecord } from '../sim/species';
import { PROGRAMS, logVariance, type KnowledgeState, type SurveyResult } from '../sim/monitoring';
import { siteHasSpinnerDolphins } from '../sim/dolphins';
import { siteHasReefResidents } from '../sim/reefResidents';
import { sanctuaryChlorophyll } from '../sim/systems/plankton';
import { ALERT_LEVELS, ALERT_MEANINGS, type AlertLevel } from '../sim/heatStress';
import { REEF_DIETS } from '../sim/predation';
import { REEF_SPECIES } from '../sim/systems/fish';
import { heatStressToday } from '../sim/systems/heatStress';
import { DHW_WINDOW_DAYS } from '../sim/heatStress';
import { reefBleached } from '../sim/coral';
import { reefOf } from '../sim/data/sites';
import { runwayWords } from './runway';
import { LIMIT_GROUPS, LIMIT_NAMES, LIMIT_OPTIONS, MAUI_RULE_DAY, PATROL_LEVELS, PROTECTED_CATCH_SHARE, PROTECTED_HERBIVORES, STATE_RULE_DAY, islandFishing, limitWords, patrolHoursAdded, takenAnywayOn, type LimitGroup } from '../sim/management';
import { ISLAND_HARD_BOTTOM_M2, type FishSpeciesId } from '../sim/fish';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { BLUE_MARLIN_SUPPLY } from '../sim/data/marlin/pacific';
import { DEEP7 } from '../sim/bottomfish';
import { SHRIMP_FISHING } from '../sim/shrimp';
import { EASTERN_NORTH_PACIFIC_GRAY_WHALE } from '../sim/graywhales';
import { dateLabel, epochDayOf } from '../sim/calendar';
import { fixed, whole } from './format';
import { ECONOMY } from '../sim/economy';
import { dollars } from '../sim/operations';
import { fundsLevel, monthsInHand } from '../sim/operations';

/**
 * The player's layers over the simulation (M160, the user's brief; `GDD.md` section 5, "Simple on the surface, complex
 * underneath"): the sanctuary overview's six indicators, the ecological communities, each species in depth, and the
 * few priorities of the moment. Every figure here is the simulation's, read out; none is a state of its own. A
 * population's size is what its monitoring would know (the user's choice: "What is known"), never the model's own count.
 */

/** How an indicator or a community stands: by the sources' own measures (each says which), or not yet known or built. */
export type Level = 'good' | 'fair' | 'poor' | 'unknown';

/** Which way a figure is going, by its monitoring: up, down or steady beyond the survey's own error, or not known; over several species, which way the sanctuary is going (an introduced one's read the other way, `trendOf`). */
export type Trend = 'up' | 'down' | 'steady' | 'unknown';

/** The overview's six (the user's choice: all six; Finances and Conservation progress built since M238 and M239). */
export type IndicatorId = 'reef' | 'fish' | 'biodiversity' | 'threats' | 'finances' | 'progress';

export interface Indicator {
  id: IndicatorId;
  /** "Reef health", "Fish community", "Biodiversity", "Threats", "Finances", "Conservation progress". */
  label: string;
  level: Level;
  /** The headline figure as the player reads it, e.g. "17% live coral". */
  value: string;
  trend: Trend;
  /** One sentence: why the level, and on whose measure. */
  why: string;
  /** The communities a tap goes down to. */
  communities: readonly CommunityId[];
  /** Where its level is unknown, why, as the player reads it (M237: "Survey needed", "Baseline incomplete", ...). */
  unknownAs?: string;
}

/** What a monitoring program knows of a species' numbers (the brief's four states; unknown is never zero): the simulation's (`monitoring.ts`). */
export type { KnowledgeState } from '../sim/monitoring';

export interface Known {
  state: KnowledgeState;
  /** As the player reads it: "about 92 metric tons of fish (95% range 72 to 120), counted 14 July 2024", "about 11,278 whales (95% interval 4,049 to 31,412), 2020", "lives here; not counted", "not at this sanctuary". */
  text: string;
  trend: Trend;
  /** Who counts it, how, and how often. */
  source: string;
}

export interface CommunitySummary {
  id: CommunityId;
  name: string;
  /** What it does for the ecosystem. */
  role: string;
  level: Level;
  trend: Trend;
  members: readonly { id: string; name: string; known: Known }[];
  /** The threats on it now, in words, each the simulation's (heat, fishing, an introduced predator, ...). */
  threats: readonly string[];
  /** Where its level is unknown, why (M237, `unknownWords`). */
  unknownAs?: string;
}

export interface SpeciesDetail {
  record: SpeciesRecord;
  /** Its common name with its Hawaiian name. */
  title: string;
  /** The community's name. */
  community: string;
  known: Known;
  /** What it eats and what eats it, as the simulation's diets hold them (names), or none held. */
  eats: readonly string[];
  eatenBy: readonly string[];
  threats: readonly string[];
  /** The deep panel's rows, label and text: its names, community and home, its numbers and who counts them, its diet, threats and model. */
  rows: readonly (readonly [string, string])[];
}

export interface Priority {
  severity: 'critical' | 'warning' | 'good' | 'info';
  title: string;
  detail: string;
  /** The world's day it arose. */
  day: number;
  /** The indicator it belongs to, where one does. */
  indicator: IndicatorId | null;
}

/** The diets the simulation holds, as the species detail reads them: what each reef stock eats and what eats it. */
function dietLinks(): { eats: Map<string, string[]>; eatenBy: Map<string, string[]> } {
  const eats = new Map<string, string[]>();
  const eatenBy = new Map<string, string[]>();
  const add = (m: Map<string, string[]>, k: string, v: string): void => {
    const got = m.get(k) ?? [];
    if (!got.includes(v)) got.push(v);
    m.set(k, got);
  };
  for (const d of REEF_DIETS) {
    for (const p of d.prey) {
      add(eats, d.predator, p);
      add(eatenBy, p, d.predator);
    }
  }
  return { eats, eatenBy };
}
const DIET_LINKS = dietLinks();

/** A threat on a species now, by its kind: the fishery, an introduced predator, the heat, or the bleaching it brings. */
interface Threat {
  kind: 'fishing' | 'take' | 'introduced' | 'heat' | 'bleaching';
  /** The share of the sizes taken caught a year, for the fishery. */
  share?: number;
  /** The introduced predator's name. */
  by?: string;
  text: string;
}

/**
 * The threats on a species now, each one the simulation carries (a threat it does not model is not listed): a reef
 * stock's fishing (its rate a year, NOAA's for the main islands, `FishSupply.fishingPerYear`) and an introduced predator
 * eating it; the blue marlin's fishing and the gray whales' hunt (review M162: the populations of no sanctuary's take
 * was left out); the coral's heat (NOAA Coral Reef Watch's alert level, `heatStressToday`) and its bleaching.
 */
function threatItems(world: World, id: string): Threat[] {
  const s = speciesOf(id);
  const sanctuary = world.sanctuaries[0];
  const out: Threat[] = [];
  if (s.model.kind === 'reef stock') {
    const fishId = s.model.id;
    const sp = REEF_SPECIES.find((x) => x.life.id === fishId);
    const m = sp?.life.naturalPerYear ?? 0;
    // Each island's fishing, a protected island's the share of its catch that goes on (M165's reviewer: the stock's
    // whole rate was told on a protected reef).
    // Each island's rate under its rules (M170: Hawaiʻi's differ by island), the open islands' and the protected's apart.
    const all = (sp && islandFishing(sp, sanctuary?.management ?? null, epochDayOf(world))?.map((f) => f.perYear)) ?? [sp?.supply.fishingPerYear ?? 0];
    const protectedOn = (i: number): boolean => sp !== undefined && PROTECTED_HERBIVORES.includes(sp.life.id) && sanctuary?.management?.herbivoreProtection[i] === true;
    const open = all.filter((_, i) => !protectedOn(i));
    const kept = all.filter((_, i) => protectedOn(i));
    // An instantaneous rate a year as the share of the sizes fully taken caught in a year, Baranov's catch equation
    // with the natural deaths competing, F/(F+M)·(1 − e^(−(F+M))) (review M160's reviewer: 1 − e^(−F) overstated it);
    // the predators' take, which varies with their numbers, competes too and is left out (open).
    const caught = (f: number): number => (f > 0 ? (f / (f + m)) * (1 - Math.exp(-(f + m))) : 0);
    const most = Math.max(...(open.length > 0 ? open : kept));
    const share = caught(most);
    // Under 1% where some is still taken, never 0% (review M181: patrolled closely, a protected island's 0.4% read "0%").
    const protectedPercent = 100 * caught(Math.max(...kept, 0));
    const where = open.length > 0 && kept.length > 0 ? `, ${protectedPercent > 0 && protectedPercent < 0.5 ? 'under 1%' : `${fixed(protectedPercent, 0)}%`} where it is protected` : '';
    if (most > 0) out.push({ kind: 'fishing', share, text: `fished: about ${fixed(100 * share, 0)}% of the sizes taken are caught each year${where}` });
    for (const p of DIET_LINKS.eatenBy.get(fishId) ?? []) {
      const by = sentenceName(speciesOf(p));
      if (speciesOf(p).community === 'introduced') out.push({ kind: 'introduced', by, text: `eaten by the introduced ${by}` });
    }
  }
  // The Deep 7's catch (M192): the assessment's harvest rate, a share of the year's starting weight.
  if (s.model.kind === 'deep7' && DEEP7.harvest > 0) out.push({ kind: 'fishing', share: DEEP7.harvest, text: `fished: about ${fixed(100 * DEEP7.harvest, 0)}% of the stock's weight caught each year (NOAA's 2024 assessment, its last year)` });
  // The shrimp's (M193): the main islands' landings over their stock, a share of its weight a year.
  if (s.model.kind === 'deepwater shrimp' && SHRIMP_FISHING > 0) out.push({ kind: 'fishing', share: SHRIMP_FISHING, text: `fished: about ${fixed(100 * SHRIMP_FISHING, 0)}% of its trapped sizes' weight caught each year (the main islands' landings, 2011 to 2015)` });
  if (s.id === 'blueMarlin' && BLUE_MARLIN_SUPPLY.fishing > 0) out.push({ kind: 'take', text: 'fished across the Pacific by its fleets, every fish of a year and over (fitted to the ISC\'s 2026 assessment)' });
  if (s.id === 'grayWhale' && EASTERN_NORTH_PACIFIC_GRAY_WHALE.huntPerYear > 0) out.push({ kind: 'take', text: `hunted off Chukotka, ${fixed(EASTERN_NORTH_PACIFIC_GRAY_WHALE.huntPerYear, 0)} whales struck a year (2018 to 2022, Scordino et al. 2024)` });
  if (s.model.kind === 'coral' && sanctuary) {
    const heat = heatStressToday(sanctuary, epochDayOf(world));
    if (heat && heat.level !== 'No stress') out.push({ kind: 'heat', text: `heat stress: ${heat.level.toLowerCase()} (NOAA Coral Reef Watch's measure)` });
    const reef = reefOf(sanctuary);
    if (reef && sanctuary.coral) {
      const bleached = reefBleached(reef, sanctuary.coral);
      if (bleached >= 0.5) out.push({ kind: 'bleaching', text: `${fixed(bleached, 0)}% of the live coral bleached` });
    }
  }
  return out;
}

/** The threats on several species, one line a kind: how many of them the fishery takes and at most how hard, which introduced predator eats how many, the heat and the bleaching. */
function threatsOver(world: World, ids: readonly string[]): string[] {
  const all = ids.flatMap((id) => threatItems(world, id));
  const fished = all.filter((t) => t.kind === 'fishing');
  const out: string[] = [];
  if (fished.length > 0) {
    const most = Math.max(...fished.map((t) => t.share ?? 0));
    out.push(`fished: ${fished.length} species, up to ${fixed(100 * most, 0)}% of the sizes taken caught a year`);
  }
  for (const by of new Set(all.filter((t) => t.kind === 'introduced').map((t) => t.by as string))) {
    const n = all.filter((t) => t.kind === 'introduced' && t.by === by).length;
    out.push(`the introduced ${by} eats ${n} species here`);
  }
  for (const t of all) if ((t.kind === 'take' || t.kind === 'heat' || t.kind === 'bleaching') && !out.includes(t.text)) out.push(t.text);
  return out;
}

/**
 * The priorities of the moment (at most four, the brief's "two to four"): the warnings and the critical events of the
 * last twelve weeks (`DHW_WINDOW_DAYS`, the span NOAA's heat stress counts), the latest of each kind, the most severe
 * first, then the latest. Routine events (a new ENSO year, a heatwave's end) stay in the record, not here; an end
 * closes its kind, so a heatwave over is no longer a priority (review M160's reviewer: its warning stayed twelve weeks).
 */
export function priorities(world: World): Priority[] {
  const since = world.day - DHW_WINDOW_DAYS;
  const rank = { critical: 0, warning: 1, good: 2, info: 3 } as const;
  const seen = new Set<string>();
  const out: Priority[] = [];
  for (let i = world.log.length - 1; i >= 0; i--) {
    const e = world.log[i] as GameEvent;
    if (e.day < since) break;
    const kind = eventKind(e.title);
    if (seen.has(kind)) continue;
    seen.add(kind);
    if (e.severity === 'info') continue;
    out.push({ severity: e.severity, title: e.title, detail: e.detail, day: e.day, indicator: kind === 'heatwave' ? 'threats' : 'reef' });
  }
  return out.sort((a, b) => rank[a.severity] - rank[b.severity] || b.day - a.day).slice(0, 4);
}

/** An event's kind, from its title, so the latest of each stands for the others. */
function eventKind(title: string): string {
  if (/heatwave/i.test(title)) return 'heatwave';
  if (/Alert level|heat stress/i.test(title)) return 'heat stress';
  if (/bleaching/i.test(title)) return 'bleaching';
  if (/killed/i.test(title)) return 'coral killed';
  return title;
}

/** A figure as the player reads it, without its unit: a percent to a tenth, chlorophyll to a hundredth, a few hundred whole, a thousand and up to two figures. */
function figure(value: number, unit: string): string {
  if (unit.startsWith('%')) return fixed(value, 1);
  if (unit.startsWith('mg')) return fixed(value, 2);
  // A weight under ten metric tons to a tenth, so a small stock does not read as none (review M162).
  if (unit.startsWith('metric tons') && value < 10) return fixed(value, 1);
  const digits = value >= 1000 ? Math.pow(10, Math.floor(Math.log10(value)) - 1) : 1;
  return whole(Math.round(value / digits) * digits);
}

/** A figure with its unit, the unit for one where the figure reads one ("1 seal", "1 tonne of fish"; review M162). */
function amount(value: number, unit: string): string {
  if (unit.startsWith('%')) return `${figure(value, unit)}${unit}`;
  const f = figure(value, unit);
  return `${f} ${f === '1' ? unit.replace(/s( of .*)?$/, '$1') : unit}`;
}

/**
 * The trend between a program's last two surveys: beyond both their errors (two standard errors of the log ratio) or,
 * for a count, any change. A survey of none after some is down, as some after none is up, and none twice is down, a
 * species gone not holding (review M160's reviewers: a stock surveyed at none read as unknown, then twice as steady).
 */
export function surveyTrend(list: readonly SurveyResult[], trendSigma = 0): Trend {
  const [a, b] = [list.at(-2), list.at(-1)];
  if (!a || !b) return 'unknown';
  if (a.value <= 0 || b.value <= 0) return b.value > 0 ? 'up' : 'down';
  const [sa, sb] = [logVariance(a.cv), logVariance(b.cv)];
  const z = Math.log(b.value / a.value);
  const limit = 1.96 * Math.sqrt(sa + sb + trendSigma * trendSigma);
  return z > limit ? 'up' : z < -limit ? 'down' : 'steady';
}

/** What is known of a species' numbers (the user's choice: "What is known"), from its program and the surveys taken. */
export function knownOf(world: World, id: string): Known {
  const p = PROGRAMS[id];
  if (!p) return { state: 'not surveyed', text: 'not surveyed', trend: 'unknown', source: 'no program counts it' };
  // A species the sanctuary does not hold is surveyed by no program here (review M162: a world opened away from Maui
  // Nui was told the jacks "live here" and Maui's programs counted on).
  if (!speciesHere(world).some((r) => r.id === id)) return { state: 'not surveyed', text: 'not at this sanctuary', trend: 'unknown', source: `elsewhere: ${p.source}` };
  if (p.state === 'present') return { state: 'present', text: 'lives here; not counted', trend: 'unknown', source: p.source };
  if (p.published) {
    const range = p.published.low !== null && p.published.high !== null ? ` (95% interval ${whole(p.published.low)} to ${whole(p.published.high)})` : '';
    return { state: 'estimated', text: `about ${whole(p.published.value)} ${p.unit}${range}, ${p.published.year}`, trend: 'unknown', source: `${p.source}; its trend: ${p.published.trend}` };
  }
  if (!p.schedule) {
    // Seen without a schedule (the satellite's chlorophyll): read live.
    const s = world.sanctuaries[0];
    const value = s ? sanctuaryChlorophyll(world, s) : null;
    return { state: 'monitored', text: value === null ? 'not seen' : `${amount(value, p.unit)} today`, trend: 'unknown', source: p.source };
  }
  const list = world.monitoring[id] ?? [];
  const last = list.at(-1);
  if (!last) return { state: 'monitored', text: 'not yet counted', trend: 'unknown', source: p.source };
  const sigma = Math.sqrt(logVariance(last.cv));
  const [low, high] = [figure(last.value * Math.exp(-1.96 * sigma), p.unit), figure(last.value * Math.exp(1.96 * sigma), p.unit)];
  // No range where its ends read alike (a seal or two: "1 to 1"; review M162).
  const range = sigma > 0 && low !== high ? ` (95% range ${low} to ${high})` : '';
  return { state: 'monitored', text: `about ${amount(last.value, p.unit)}${range}, counted ${dateLabel(last.day)}`, trend: surveyTrend(list, p.trendSigma), source: p.source };
}

/** The species at the player's sanctuary: its site's populations, and the populations of no sanctuary a source places at its site (`SpeciesRecord.atSites`). */
export function speciesHere(world: World): SpeciesRecord[] {
  const s = world.sanctuaries[0];
  if (!s) return [];
  return SPECIES.filter((r) => {
    switch (r.model.kind) {
      case 'reef stock': return s.fish !== null;
      case 'honu': return s.turtles !== null;
      case 'hawksbill': return s.turtles !== null;
      case 'monk seal': return s.seals !== null;
      case 'humpback': return s.whales !== null;
      case 'tiger shark': return s.sharks !== null;
      case 'whitetip': return s.sharks !== null;
      case 'spinner dolphin': return siteHasSpinnerDolphins(s.siteId);
      case 'counted': return siteHasReefResidents(s.siteId);
      case 'deep7': return s.slope !== null;
      case 'deepwater shrimp': return s.slope !== null;
      case 'coral': return s.coral !== null;
      case 'plankton': return true;
      case 'migrant': return r.atSites?.includes(s.siteId) === true;
    }
  });
}

/** How the simulation holds a species, in a sentence. */
function modelText(r: SpeciesRecord): string {
  switch (r.model.kind) {
    case 'reef stock': return 'simulated month by month of age on each of Maui Nui\'s four islands, born, grown, fished, eaten and dead as its sources measure';
    case 'honu': return 'simulated year by year of age, at home and away breeding';
    case 'hawksbill': return 'simulated year by year of age, at home and away nesting';
    case 'monk seal': return 'simulated year by year of age, pups and all';
    case 'humpback': return 'simulated year by year of age, the whole Hawaiʻi population, coming and going with the season';
    case 'tiger shark': return 'simulated year by year of age, pups and all, at the least number the one count tied to an area gives: no one has counted Maui Nui\'s';
    case 'whitetip': return 'simulated year by year of age, pups and all, at NOAA\'s divers\' count over the reef, its life the Great Barrier Reef\'s: no Hawaiian whitetip has been aged';
    case 'spinner dolphin': return 'not simulated: no count of Maui Nui\'s alone exists, so the game holds no number';
    case 'counted': return 'not simulated: counted as NOAA\'s divers found it at Maui Nui, its life not yet measured well enough to step it';
    case 'deepwater shrimp': return 'simulated month by month of age, each sex growing, dying and fished day by day on the slopes\' floor as its sources measure, its recruits steady through the year';
    case 'deep7': return 'simulated as NOAA\'s assessment holds the Deep 7, one stock\'s weight from fishing year to fishing year, growing back and fished, each species its share of NOAA\'s survey';
    case 'coral': return 'simulated as live cover on each island\'s reef at three depths, lobe, finger, rice, cauliflower and the rest, growing and bleaching, each kind spawning on its own nights and dying in the heat as its kind does';
    case 'plankton': return 'simulated as a nutrient and plankton model in the sanctuary\'s water and every region\'s';
    case 'migrant': return 'simulated year by year of age, the whole population, wherever it swims';
  }
}

/** A species' home as the player reads it. */
const HOMES: Readonly<Record<SpeciesRecord['home'], string>> = { reef: 'the reef', slope: 'the reef\'s slope', 'insular shelf': 'the islands\' shelf, its floor most at 50 to 100 m', 'deep slope': 'the deep slope\'s hard bottom, 100 to 400 m', 'lower slope': 'the lower slope\'s floor, most at 460 to 640 m', 'inshore by day': 'the bays and channels by day, the open water by night', 'cleaning stations': 'the open water, and the reef\'s cleaning stations', blue: 'the open water', 'open sea': 'the open sea' };

/** A species in depth (the third layer): what the panel and the field guide show. */
export function speciesRecord(world: World, id: string): SpeciesDetail {
  const record = speciesOf(id);
  const names = (ids: readonly string[]): string[] => ids.map((x) => displayName(speciesOf(x)));
  const known = knownOf(world, id);
  const eats = names(DIET_LINKS.eats.get(id) ?? []);
  const eatenBy = names(DIET_LINKS.eatenBy.get(id) ?? []);
  // A species the sanctuary does not hold has none of the sanctuary's threats (review M162's reviewer: a reef fish's at
  // Vavaʻu read Maui Nui's fishing); a migrant's take is its population's, wherever it is read.
  const here = speciesHere(world).some((r) => r.id === id);
  const threats = threatItems(world, id).filter((t) => here || t.kind === 'take').map((t) => t.text);
  const community = COMMUNITIES.find((c) => c.id === record.community)?.name ?? record.community;
  const rows: [string, string][] = [
    ['Scientific name', record.scientific],
    ['Community', `${community}: ${record.communitySource}`],
    ['Home', HOMES[record.home]],
    ['Numbers', `${known.text} (${known.state})`],
    ['Trend', known.trend === 'unknown' ? 'not known' : known.trend],
    ['Counted by', known.source],
    ...(eats.length > 0 ? [['Eats (in the simulation)', eats.join(', ')] as [string, string]] : []),
    ...(eatenBy.length > 0 ? [['Eaten by (in the simulation)', eatenBy.join(', ')] as [string, string]] : []),
    ['Threats', threats.length > 0 ? threats.join('; ') : 'none the simulation carries yet'],
    ['In the game', modelText(record)],
  ];
  return { record, title: displayName(record), community, known, eats, eatenBy, threats, rows };
}

/**
 * A member's trend as the sanctuary reads it: a native species' own, an introduced one's turned over, since fewer of
 * the introduced roi is the reef's gain (review M160's reviewer: its decline read as a worse fish community).
 */
interface Member { id: string; known: Known; introduced: boolean }
const worse = (m: Member): boolean => m.known.trend === (m.introduced ? 'up' : 'down');
const better = (m: Member): boolean => m.known.trend === (m.introduced ? 'down' : 'up');

/** A community's members as the levels read them. */
const asRead = (c: { id: CommunityId; members: readonly { id: string; known: Known }[] }): Member[] => c.members.map((m) => ({ id: m.id, known: m.known, introduced: c.id === 'introduced' }));

/** A level from how many of the counted species are worsening beyond their surveys' error (SET BY HAND, a reading rule: none good, under half fair, half or more poor). */
function levelOfDeclines(members: readonly Member[]): Level {
  const counted = members.filter((m) => m.known.trend !== 'unknown');
  if (counted.length === 0) return 'unknown';
  const down = counted.filter(worse).length;
  return down === 0 ? 'good' : down < counted.length / 2 ? 'fair' : 'poor';
}

/**
 * Why a level is unknown, in words the player can act on (M237, the overhaul's brief: "Replace repetitive 'Not known'
 * labels with meaningful information states"): no member counted by any program, "Survey needed" (or "Estimate only"
 * where a published estimate is all); counted by a program not yet surveyed, "Awaiting monitoring results"; surveyed
 * once, a trend needing two, "Baseline incomplete". Each read from the monitoring's own record (`world.monitoring`).
 */
export function unknownWords(world: World, members: readonly { id: string; known: Known }[]): string {
  const monitored = members.filter((m) => m.known.state === 'monitored');
  // M262 (the brief: no unexplained "Baseline incomplete"): each says what it means.
  if (monitored.length === 0) return members.some((m) => m.known.state === 'estimated') ? 'Published estimate only' : 'Not monitored here';
  return monitored.some((m) => (world.monitoring[m.id]?.length ?? 0) > 0) ? 'One count so far, no trend yet' : 'Not counted yet';
}

/** A trend over several, as the sanctuary reads it: down if any worsens, else up if any betters, else steady, else not known. */
function trendOf(members: readonly Member[]): Trend {
  if (members.some(worse)) return 'down';
  if (members.some(better)) return 'up';
  return members.some((m) => m.known.trend === 'steady') ? 'steady' : 'unknown';
}

/** The ecological communities at the player's sanctuary (the second layer), in `COMMUNITIES`' order. */
export function communitiesOf(world: World): CommunitySummary[] {
  const here = speciesHere(world);
  return COMMUNITIES.flatMap((c) => {
    const members = here.filter((r) => r.community === c.id).map((r) => ({ id: r.id, name: displayName(r), known: knownOf(world, r.id) }));
    if (members.length === 0) return [];
    const threats = threatsOver(world, members.map((m) => m.id));
    const read = asRead({ id: c.id, members });
    const level = levelOfDeclines(read);
    return [{ id: c.id, name: c.name, role: c.role, level, trend: trendOf(read), members, threats, ...(level === 'unknown' ? { unknownAs: unknownWords(world, members) } : {}) }];
  });
}

/** The heat on the reef today, by NOAA Coral Reef Watch's alert levels (cited in `heatStress.ts`), and its rank among them. */
function heatToday(world: World): { level: AlertLevel; rank: number } | null {
  const s = world.sanctuaries[0];
  const heat = s ? heatStressToday(s, epochDayOf(world)) : null;
  return heat ? { level: heat.level, rank: ALERT_LEVELS.indexOf(heat.level) } : null;
}

/**
 * The sanctuary overview's six indicators (the first layer; the user's choice: all six, Finances and Conservation
 * progress read from the operations and the campaign since M238 and M239). Reef health: the coral's last survey and today's heat (poor from NOAA's Alert level 1,
 * "risk of reef-wide bleaching", or with the cover down from the world's first survey beyond both surveys' error; fair
 * at a watch or warning, or down from the survey before; SET BY HAND, a reading rule, review M160's reviewer: a reef
 * steady after a die-off read good); Fish community: the native reef fish's trends, Biodiversity: every counted
 * species', an introduced one's turned over (`levelOfDeclines`); Threats: the heat and the pressures the simulation
 * carries, the introduced predator among them.
 */
export function overviewOf(world: World): Indicator[] {
  const communities = communitiesOf(world);
  const heat = heatToday(world);
  const coral = knownOf(world, 'coral');
  const coralList = world.monitoring.coral ?? [];
  const first = coralList[0];
  const last = coralList.at(-1);
  const lastCover = last?.value;
  const lost = first && last && first !== last && surveyTrend([first, last]) === 'down';
  const acute = heat !== null && heat.rank >= ALERT_LEVELS.indexOf('Alert level 1');
  const reefLevel: Level = acute || lost ? 'poor'
    : (heat && heat.rank >= 1) || coral.trend === 'down' ? 'fair' : coral.state === 'monitored' && lastCover !== undefined ? 'good' : 'unknown';
  const fishCommunities = communities.filter((c) => c.id === 'reefHerbivores' || c.id === 'reefPredators');
  const fish = fishCommunities.flatMap(asRead);
  const counted = fish.filter((m) => m.known.trend !== 'unknown');
  // Counted once or more, a trend or not (review M162: the fish read "not yet counted" for 560 days after their first count).
  const surveyed = fish.filter((m) => (world.monitoring[m.id]?.length ?? 0) > 0);
  const hasReef = communities.some((c) => c.id === 'coral');
  const all = communities.flatMap(asRead);
  const allKnowns = all.map((m) => m.known);
  const here = communities.reduce((a, c) => a + c.members.length, 0);
  const threats = threatsOver(world, communities.flatMap((c) => c.members.map((m) => m.id)));
  return [
    {
      id: 'reef', label: 'Reef health', level: reefLevel,
      value: !hasReef ? 'no reef in the game here' : lastCover === undefined ? 'not yet surveyed' : `${fixed(lastCover, 1)}% live coral`,
      trend: coral.trend,
      why: !hasReef ? 'the game holds no reef at this sanctuary yet' : `${coral.text}${heat ? `; heat today: ${heat.level.toLowerCase()} (${ALERT_MEANINGS[heat.level]}, NOAA Coral Reef Watch)` : ''}`,
      communities: hasReef ? ['coral'] : [],
      ...(reefLevel === 'unknown' ? { unknownAs: lastCover === undefined ? 'Not monitored here' : 'One count so far, no trend yet' } : {}),
    },
    {
      id: 'fish', label: 'Fish community', level: levelOfDeclines(fish),
      value: fish.length === 0 ? 'no reef fish in the game here'
        : counted.length > 0 ? `${counted.filter((m) => !worse(m)).length} of ${counted.length} counted species holding`
        : surveyed.length > 0 ? `${surveyed.length} species counted once, no trend yet` : 'not yet counted',
      trend: trendOf(fish),
      why: fish.length === 0 ? 'the game holds no reef fish at this sanctuary yet' : 'the native reef fish NOAA\'s divers count, each against its own last survey and its error; the jacks and the octopus are not counted, and the introduced roi is a threat (below)',
      communities: fishCommunities.map((c) => c.id),
      ...(levelOfDeclines(fish) === 'unknown' ? { unknownAs: unknownWords(world, fishCommunities.flatMap((c) => c.members)) } : {}),
    },
    {
      id: 'biodiversity', label: 'Biodiversity', level: levelOfDeclines(all),
      value: `${here} species known here, ${allKnowns.filter((k) => k.state === 'monitored').length} of them monitored`,
      trend: trendOf(all),
      why: 'every species the sanctuary holds, and how many of those its programs count are declining (or, the introduced, rising)',
      communities: communities.map((c) => c.id),
      ...(levelOfDeclines(all) === 'unknown' ? { unknownAs: unknownWords(world, communities.flatMap((c) => c.members)) } : {}),
    },
    {
      id: 'threats', label: 'Threats', level: acute ? 'poor' : threats.length > 0 ? 'fair' : 'good',
      value: threats.length === 0 ? 'none the simulation carries' : `${threats.length} at work`,
      trend: 'unknown',
      why: threats.length === 0 ? 'no threat the simulation carries acts here today' : threats.join('; '),
      communities: communities.filter((c) => c.threats.length > 0).map((c) => c.id),
    },
    financesOf(world),
    progressOf(world, communities),
  ];
}

/**
 * The conservancy's finances (M238): its funds and the months of running costs they hold, good from six, fair from two,
 * poor below or in debt (JUDGED: a season's surveys, and the July grant within reach); its trend the last month's.
 */
function financesOf(world: World): Indicator {
  const ops = world.operations;
  const months = monthsInHand(world);
  const level: Level = fundsLevel(world);
  const last = ops.months.at(-1);
  const before = ops.months.at(-2);
  const trend: Trend = !last || !before ? 'unknown' : last.funds > before.funds ? 'up' : last.funds < before.funds ? 'down' : 'steady';
  return {
    id: 'finances', label: 'Finances', level, value: `${dollars(ops.funds)} in hand`, trend,
    why: ops.funds < 0 ? 'the conservancy is in debt, its patrols lapsed' : `${Number.isFinite(months) ? runwayWords(world) : 'no running costs'}; the operating grant comes each 1 July`,
    communities: [],
  };
}

/** An island's reef as the management panel reads it (M165). */
export interface IslandManagement {
  island: ReefIsland;
  name: string;
  herbivoresProtected: boolean;
  /** Its reef's hard bottom, km² (`ISLAND_HARD_BOTTOM_M2`), and its share of Maui Nui's. */
  reefKm2: number;
  share: number;
  /** Its patrols (M171): the level in force (`level`, 0 Hawaiʻi's officers alone), each level's words, the share of what a rule forbids still taken at each, and the officers' hours a year each adds beside Hawaiʻi's, for `set_patrol`. */
  patrol: { level: number; until: number | null; choices: readonly string[]; takenAnyway: readonly number[]; hoursAdded: readonly number[]; costPerYear: readonly number[] };
}

/** A sanctuary's management as the panel reads it (M165), or none where it has nothing to manage. */
export interface ManagementReadout {
  islands: readonly IslandManagement[];
  /** The share of the sanctuary's reef, by its hard bottom, whose herbivores are protected (an area, not the fish's numbers, which no one counts island by island). */
  protectedShare: number;
  /** The share of a protected stock's catch that goes on under Hawaiʻi's officers alone (`PROTECTED_CATCH_SHARE`), and on whose measure; an island's own, under its patrols, in its `patrol`. */
  catchKept: number;
  catchKeptSource: string;
  /** The species a Herbivore Protection Area protects, with what is known of them. */
  protectedSpecies: readonly { id: string; name: string; known: Known }[];
  /** Each fish group's size and bag limits (M170): Hawaiʻi's rule on the day, and the sanctuary's choices, the one in force (`option`, 0 Hawaiʻi's), for `set_limit`. */
  limits: readonly GroupLimits[];
}

export interface GroupLimits {
  group: LimitGroup;
  name: string;
  hawaii: string;
  bag: { option: number; choices: readonly string[] };
  size: { option: number; choices: readonly string[] };
}

/** Hawaiʻi's rule for a group on a day, in words (`hawaiiRule`). */
function hawaiiWords(group: LimitGroup, epochDay: number): string {
  const statewide = epochDay >= STATE_RULE_DAY;
  const maui = epochDay >= MAUI_RULE_DAY;
  if (group === 'uhu') {
    if (statewide) return '2 a fisher a day and 10 inches at least, the redlip 14; none but the redlip taken for sale, off Maui a commercial trip taking more of it; on Maui every fisher held to 2, and no terminal-phase redlip taken';
    return maui ? '12 inches at least, no bag limit; on Maui 2 a fisher a day, 10 inches at least, the redlip 14, and no terminal-phase redlip taken' : '12 inches at least, no bag limit';
  }
  if (group === 'kala') return statewide ? '4 a fisher a day (commercial fishers under a yearly limit) and 14 inches at least' : '14 inches at least, no bag limit';
  if (group === 'jacks') return '20 a fisher a day (commercial fishers excepted) and 10 inches at least';
  // The moano kea (M179): Maui island's rule alone (HAR 13-95.1-20), none elsewhere.
  if (group === 'goatfish') return maui ? 'no size or bag limit; on Maui 12 inches at least and 2 a fisher a day' : 'no size or bag limit';
  return 'a pound at least';
}

/** The sanctuary's management, read for its panel (M165): each island's reef and its protection, and the protected species. */
export function managementOf(world: World): ManagementReadout | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const all = ISLAND_HARD_BOTTOM_M2.reduce((a, b) => a + b, 0);
  const islands = REEF_ISLANDS.map((island, i) => {
    const m2 = ISLAND_HARD_BOTTOM_M2[i] as number;
    const patrol = {
      level: m.patrols[i] ?? 0, choices: PATROL_LEVELS.map((l) => l.name), takenAnyway: PATROL_LEVELS.map((_, level) => takenAnywayOn(island, level)),
      until: m.patrolUntil[i] ?? null,
      hoursAdded: PATROL_LEVELS.map((_, level) => patrolHoursAdded(island, level)),
      // What the conservancy pays for them a year (M238, `economy.ts`).
      costPerYear: PATROL_LEVELS.map((_, level) => Math.round(patrolHoursAdded(island, level) * ECONOMY.officerHour)),
    };
    return { island, name: ISLAND_NAMES[island], herbivoresProtected: m.herbivoreProtection[i] === true, reefKm2: m2 / 1e6, share: m2 / all, patrol };
  });
  return {
    islands,
    protectedShare: islands.reduce((a, x) => a + (x.herbivoresProtected ? x.share : 0), 0),
    catchKept: PROTECTED_CATCH_SHARE,
    catchKeptSource: 'Waikīkī\'s reserve, Oʻahu, 1998 to 2001, its catch inside against the fished reef beside it (Meyer 2003); no Maui Nui reserve\'s has been measured',
    protectedSpecies: PROTECTED_HERBIVORES.map((id) => ({ id, name: displayName(speciesOf(id)), known: knownOf(world, id) })),
    limits: LIMIT_GROUPS.map((group) => ({
      group,
      name: LIMIT_NAMES[group],
      hawaii: hawaiiWords(group, epochDayOf(world)),
      bag: { option: m.limits[group].bag, choices: LIMIT_OPTIONS[group].bags.map((_, i) => limitWords(group, 'bag', i)) },
      size: { option: m.limits[group].size, choices: LIMIT_OPTIONS[group].sizes.map((_, i) => limitWords(group, 'size', i)) },
    })),
  };
}

/** The share of the protected islands' catch still taken, as their patrols keep it (M171's reviewer: told at Hawaiʻi's 31% whatever the patrols): one share, or from the least to the most. */
function takenWords(on: readonly IslandManagement[]): string {
  const shares = on.map((x) => Math.round(100 * (x.patrol.takenAnyway[x.patrol.level] as number)));
  const least = Math.min(...shares);
  const most = Math.max(...shares);
  return least === most ? `about ${least}%` : `about ${least}% to ${most}%`;
}

/**
 * Conservation progress (the user's choice: it shows what is protected, M165): the share of the reef, by its hard
 * bottom, whose herbivores are protected, and the protected species' surveys' trend. Its level not known: no goal is
 * set yet (goals come with Phase 11's awards and scenarios).
 */
function progressOf(world: World, communities: readonly CommunitySummary[]): Indicator {
  const m = managementOf(world);
  if (!m) return { id: 'progress', label: 'Conservation progress', level: 'unknown', value: 'nothing to manage here yet', trend: 'unknown', why: 'the game holds no reef fish at this sanctuary yet', communities: [], unknownAs: 'Nothing to manage here' };
  const on = m.islands.filter((x) => x.herbivoresProtected);
  const herbivores = communities.filter((c) => c.id === 'reefHerbivores').flatMap(asRead).filter((x) => PROTECTED_HERBIVORES.includes(x.id as FishSpeciesId));
  return {
    id: 'progress', label: 'Conservation progress', level: 'unknown',
    value: on.length === 0 ? 'nothing protected yet' : `herbivores protected on ${fixed(100 * m.protectedShare, 0)}% of the reef`,
    trend: on.length === 0 ? 'unknown' : trendOf(herbivores),
    why: on.length === 0
      ? 'no island\'s herbivores are protected yet'
      : `the parrotfish, kala, nenue and manini protected on ${on.map((x) => x.name).join(', ')}, ${takenWords(on)} of their catch still taken there (${m.catchKeptSource}${on.some((x) => x.patrol.level > 0) ? '; under patrols, DOCARE\'s Maui unit\'s' : ''}); no goal is set yet`,
    communities: on.length === 0 ? [] : ['reefHerbivores'],
    unknownAs: 'No goal set yet',
  };
}

/** A field guide's entry (the brief's optional guide): enough to list and find a species; its page is `speciesRecord`'s. */
export interface GuideEntry {
  id: string;
  title: string;
  scientific: string;
  home: SpeciesRecord['home'];
  state: KnowledgeState;
}

/** A name for searching: lower case, its marks (the ʻokina, the kahakō, any accent) taken off. */
function searchable(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f\u02bb\u2018\u2019']/g, '').toLowerCase();
}

/**
 * The field guide (M160): every species the game holds, grouped by community in `COMMUNITIES`' order, those matching
 * the search (any of its names, its marks optional) where one is given. Nothing in it is required of the player.
 */
export function fieldGuide(world: World, search = ''): { community: string; entries: GuideEntry[] }[] {
  const q = searchable(search.trim());
  return COMMUNITIES.flatMap((c) => {
    const entries = membersOf(c.id)
      .filter((s) => q === '' || [s.common, s.plural ?? '', s.hawaiian ?? '', s.scientific].some((n) => searchable(n).includes(q)))
      .map((s) => ({ id: s.id, title: displayName(s), scientific: s.scientific, home: s.home, state: knownOf(world, s.id).state }));
    return entries.length > 0 ? [{ community: c.name, entries }] : [];
  });
}
